import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronRight,
  Minus,
  Plus,
  ShoppingCart,
  Heart,
  Star,
  Truck,
  X,
  ChevronLeft,
  Store,
  Phone,
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from '../hooks/use-toast';
import ProductCard from '../components/ProductCard';
import GiftPopup from '../components/GiftPopup';
import SEO, { SITE_URL } from '../components/SEO';
import { cachedGet, getCached, primeCache } from '../lib/apiCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useCart();
  const { language, t } = useLanguage();
  const location = useLocation();

  // Try to hydrate the page instantly from Link state (when navigated from a
  // list view) or from the in-memory cache. This avoids any loading flash.
  const initialProduct =
    location.state?.product ||
    getCached(`/api/products/${id}`) ||
    null;

  const [product, setProduct] = useState(initialProduct);
  const [brand, setBrand] = useState(
    initialProduct?.brandId
      ? getCached(`/api/brands/${initialProduct.brandId}`) || null
      : null
  );
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [categoryPath, setCategoryPath] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);

  // Only show "loading" if we have NO data at all – first uncached visit.
  // With the cache + Link state warmup this is rarely true.
  const [loading, setLoading] = useState(!initialProduct);

  const [selectedImage, setSelectedImage] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const thumbnailsRef = useRef(null);

  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('specifications');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const [reviewForm, setReviewForm] = useState({
    userName: '',
    userEmail: '',
    rating: 5,
    comment: '',
  });

  const [submittingReview, setSubmittingReview] = useState(false);
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);

  const [installmentForm, setInstallmentForm] = useState({
    name: '',
    phone: '',
  });

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (product && product.id) {
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const fetchProduct = async () => {
    const buildCategoryPath = (categories, currentCategory) => {
      if (!currentCategory) return [];

      const byId = new Map(
        (categories || []).map((category) => [String(category.id), category])
      );

      const path = [];
      let cursor = currentCategory;
      const visited = new Set();

      while (cursor) {
        const cursorId = String(cursor.id || '');

        if (cursorId && visited.has(cursorId)) break;
        if (cursorId) visited.add(cursorId);

        path.unshift(cursor);

        if (!cursor.parentId) break;

        cursor = byId.get(String(cursor.parentId));
      }

      return path;
    };

    const findProductCategoryPath = (categories, productData) => {
      const categoryValues = [
        productData.category,
        productData.categoryId,
        ...(productData.categories || []),
      ]
        .filter(Boolean)
        .map((value) => String(value));

      const matchedCategories = (categories || []).filter((category) => {
        const possibleValues = [
          category.id,
          category.slug,
          category.name,
          category.nameRu,
        ]
          .filter(Boolean)
          .map((value) => String(value));

        return possibleValues.some((value) => categoryValues.includes(value));
      });

      if (!matchedCategories.length) return [];

      return matchedCategories
        .map((category) => buildCategoryPath(categories, category))
        .sort((a, b) => b.length - a.length)[0];
    };
    const startTime = Date.now();

    try {
      // Don't show a spinner if we already have something on screen
      if (!product) setLoading(true);
      setSelectedImage(0);
      setGalleryIndex(0);

      const productData = await cachedGet(`/api/products/${id}`);
      primeCache(`/api/products/${id}`, productData);
      setProduct(productData);

      if (productData.brandId) {
        try {
          const brandData = await cachedGet(`/api/brands/${productData.brandId}`);
          setBrand(brandData);
        } catch (error) {
          console.error('Error fetching brand:', error);
          setBrand(null);
        }
      } else {
        setBrand(null);
      }

      try {
        const categories = (await cachedGet('/api/categories')) || [];
        const path = findProductCategoryPath(categories, productData);
        const deepestCategory = path[path.length - 1] || null;

        setCategoryPath(path);
        setCategoryInfo(deepestCategory);
      } catch (error) {
        console.error('Error fetching category info:', error);
        setCategoryPath([]);
        setCategoryInfo(null);
      }

      if (productData.category) {
        try {
          const relatedData = await cachedGet(
            `/api/products?category=${encodeURIComponent(productData.category)}`
          );

          setRelatedProducts(
            (relatedData || [])
              .filter((p) => p.id !== productData.id)
              .slice(0, 15)
          );
        } catch (error) {
          console.error('Error fetching related products:', error);
          setRelatedProducts([]);
        }
      } else {
        setRelatedProducts([]);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      if (!product) setProduct(null);
    } finally {
      // No artificial delay — render the moment data is ready.
      setLoading(false);
      void startTime;
    }
  };

  const fetchReviews = async () => {
    if (!product?.id) return;

    try {
      const response = await axios.get(`${API}/products/${product.id}/reviews`);
      setReviews(response.data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviews([]);
    }
  };

  const images =
    product?.images && product.images.length > 0
      ? product.images
      : product?.image
        ? [product.image]
        : ['https://via.placeholder.com/600x600?text=No+Image'];
  useEffect(() => {
    if (!thumbnailsRef.current) return;

    const activeThumbnail = thumbnailsRef.current.querySelector(
      `[data-thumbnail-index="${selectedImage}"]`
    );

    if (activeThumbnail) {
      activeThumbnail.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [selectedImage]);

  const nextMainImage = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevMainImage = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);

    try {
      await axios.post(`${API}/products/${product.id}/reviews`, {
        ...reviewForm,
        productId: product.id,
      });

      toast({
        title: t('auth.success'),
        description: t('productDetail.reviewAdded'),
      });

      setReviewForm({
        userName: '',
        userEmail: '',
        rating: 5,
        comment: '',
      });

      fetchReviews();
      fetchProduct();
    } catch (error) {
      toast({
        title: t('auth.error'),
        description:
          error.response?.data?.detail || t('productDetail.reviewError'),
        variant: 'destructive',
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  const isOutOfStock =
    !!product && (product.inStock === false || Number(product.available ?? 0) <= 0);

  const handleAddToCart = () => {
    if (isOutOfStock) {
      toast({
        title: language === 'ru' ? 'Нет в наличии' : 'Stoc epuizat',
        description:
          language === 'ru'
            ? 'Этот товар временно отсутствует.'
            : 'Acest produs este momentan indisponibil.',
        variant: 'destructive',
      });
      return;
    }

    const productName =
      language === 'ru' && product.nameRu ? product.nameRu : product.name;

    addToCart({
      ...product,
      quantity,
    });

    toast({
      title: t('productCard.success'),
      description: `${productName} ${t('productCard.addedToCart')}`,
    });
  };

  const handleBuyNow = () => {
    if (isOutOfStock) {
      toast({
        title: language === 'ru' ? 'Нет в наличии' : 'Stoc epuizat',
        description:
          language === 'ru'
            ? 'Этот товар временно отсутствует.'
            : 'Acest produs este momentan indisponibil.',
        variant: 'destructive',
      });
      return;
    }

    addToCart({
      ...product,
      quantity,
    });

    navigate(language === 'ru' ? '/ru/checkout' : '/checkout');
  };

  const handleAddToWishlist = () => {
    const productName =
      language === 'ru' && product.nameRu ? product.nameRu : product.name;

    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);

      toast({
        title: t('productCard.success'),
        description: `${productName} ${t('productCard.removedFromWishlist') || 'eliminat din favorite'
          }`,
      });
    } else {
      addToWishlist(product);

      toast({
        title: t('productCard.success'),
        description: `${productName} ${t('productCard.addedToWishlist')}`,
      });
    }
  };

  const scrollRelatedProducts = (direction) => {
    const container = document.getElementById('related-products-container');
    const scrollAmount = 300;

    if (container) {
      if (direction === 'left') {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  const openGallery = (index) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
    document.body.style.overflow = '';
  };

  const nextImage = () => {
    setGalleryIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setGalleryIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading && !product) {
    // No data yet at all – render a transparent placeholder so the URL
    // change feels instant. The real data fills in within milliseconds.
    return <div className="min-h-screen bg-gray-50" />;
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('productDetail.notFound')}
          </h2>

          <Link to="/" className="text-[#a7cf26] font-semibold hover:underline">
            {t('productDetail.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  const productName =
    language === 'ru' && product.nameRu ? product.nameRu : product.name;


  const hasDiscount =
  product.originalPrice && Number(product.originalPrice) > Number(product.price);

const discountPercent = hasDiscount
  ? Math.round(
      ((Number(product.originalPrice) - Number(product.price)) /
        Number(product.originalPrice)) *
        100
    )
  : 0;

  // SEO data
  const seoDescription = (() => {
    const rawDesc =
      language === 'ru' && product.descriptionRu
        ? product.descriptionRu
        : product.description || '';
    const stripped = rawDesc.replace(/<[^>]+>/g, '').trim();
    if (stripped) return stripped.slice(0, 160);
    return `${productName} disponibil pe AgroSmart.md. Preț ${product.price} MDL. Livrare rapidă în toată Moldova.`;
  })();
  const seoImage =
    product.image ||
    (product.images && product.images[0]) ||
    `${SITE_URL}/favicon.png`;
  const canonicalPath = `/product/${product.slug || product.id}`;
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    description: seoDescription,
    sku: product.sku || product.id,
    image: product.images && product.images.length ? product.images : [seoImage],
    brand: brand?.name
      ? { '@type': 'Brand', name: brand.name }
      : { '@type': 'Brand', name: 'AgroSmart' },
    aggregateRating:
      product.reviews && product.reviews > 0
        ? {
          '@type': 'AggregateRating',
          ratingValue: product.rating || 5,
          reviewCount: product.reviews,
        }
        : undefined,
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}${canonicalPath}`,
      priceCurrency: 'MDL',
      price: product.price,
      availability:
        product.inStock !== false && (product.available ?? 1) > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };
  const seoBreadcrumbs = [
    { name: 'Acasă', url: '/' },
    ...categoryPath.map((category) => ({
      name:
        language === 'ru' && category.nameRu
          ? category.nameRu
          : category.name,
      url: `/category/${category.slug || category.id}`,
    })),
    { name: productName, url: canonicalPath },
  ];

  const deliveryFaqItems =
  product.faqs && product.faqs.length > 0
    ? product.faqs.map((faq) => ({
        question:
          language === 'ru' && faq.questionRu
            ? faq.questionRu
            : faq.question,
        answer:
          language === 'ru' && faq.answerRu
            ? faq.answerRu
            : faq.answer,
      }))
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={productName}
        description={seoDescription}
        image={seoImage}
        type="product"
        jsonLd={productJsonLd}
        breadcrumbs={seoBreadcrumbs}
      />

{/* Breadcrumb */}
<div className="bg-white border-b">
  <div className="w-full px-4 md:px-6 py-4">
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
      <Link to="/" className="hover:text-[#a7cf26]">
        {t('catalogCategory.breadcrumb.home')}
      </Link>

      {categoryPath.map((category) => {
        const categoryName =
          language === 'ru' && category.nameRu
            ? category.nameRu
            : category.name;

        return (
          <React.Fragment key={category.id || category.slug}>
            <ChevronRight className="w-4 h-4 flex-shrink-0" />

            <Link
              to={`/category/${category.slug || category.id}`}
              className="hover:text-[#a7cf26]"
            >
              {categoryName}
            </Link>
          </React.Fragment>
        );
      })}

      <ChevronRight className="w-4 h-4 flex-shrink-0" />

      <span className="text-gray-900 font-semibold break-words">
        {productName}
      </span>
    </div>
  </div>
</div>

      {/* Product Detail */}
      <div className="w-full px-4 md:px-4 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-12 mb-10 md:mb-12">
          {/* Images */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div
              className="relative bg-white rounded-2xl overflow-hidden mb-4 border-2 border-gray-100 cursor-pointer hover:border-[#a7cf26]/90 transition group"
              onClick={() => openGallery(selectedImage)}
            >
              {hasDiscount && (
  <span className="absolute left-3 top-3 md:left-5 md:top-5 z-30 inline-flex items-center justify-center rounded-full bg-red-600 px-2.5 py-1 text-xs md:px-3 md:py-1.5 md:text-sm font-extrabold text-white leading-none">
    -{discountPercent}%
  </span>
)}
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-[320px] md:h-[600px] object-contain"
              />

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevMainImage}
                    className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white border border-[#a7cf26] flex items-center justify-center transition"
                  >
                    <ChevronLeft className="w-6 h-6 text-gray-900" />
                  </button>

                  <button
                    type="button"
                    onClick={nextMainImage}
                    className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white border border-[#a7cf26] flex items-center justify-center transition"
                  >
                    <ChevronRight className="w-6 h-6 text-gray-900" />
                  </button>

                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full bg-black/60 text-white text-sm font-semibold">
                    {selectedImage + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="relative">
                <div
                  ref={thumbnailsRef}
                  className="flex gap-2 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      data-thumbnail-index={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`snap-start flex-shrink-0 bg-white rounded-lg overflow-hidden border-2 transition cursor-pointer hover:border-[#a7cf26]/70 ${selectedImage === idx
                        ? 'border-[#a7cf26] ring-2 ring-[#a7cf26]/25'
                        : 'border-gray-200'
                        }`}
                      style={{
                        width: 'calc((100% - 2rem) / 5)',
                      }}
                    >
                      <img
                        src={img}
                        alt=""
                        className="w-full h-[60px] md:h-[140px] object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 flex-1 min-w-0 break-words">
                {productName}
              </h1>

              <div className="hidden md:flex items-center gap-3 flex-shrink-0">
                {brand && brand.logo && (
                  <div className="bg-white border-2 border-[#a7cf26]/90 rounded-xl w-[160px] h-[64px] flex-shrink-0 flex items-center justify-center px-4">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="max-w-full max-h-[34px] object-contain"
                    />
                  </div>
                )}

                <button
                  onClick={handleAddToWishlist}
                  className="hidden md:flex bg-white border-2 border-red-500 rounded-xl p-3 md:p-4 hover:bg-red-50 transition group"
                  data-testid="product-detail-wishlist-btn"
                >
                  <Heart
                    className={`w-6 h-6 md:w-7 md:h-7 text-red-500 transition ${isInWishlist(product.id)
                      ? 'fill-red-500'
                      : 'group-hover:fill-red-500'
                      }`}
                  />
                </button>
              </div>
            </div>

            {product.sku && (
              <div className="mb-2 flex items-center gap-2 text-gray-600">
                <span className="font-semibold">{t('productDetail.sku')}:</span>
                <span className="text-gray-800">{product.sku}</span>
              </div>
            )}

            <div className="flex items-center gap-4 mb-2 flex-wrap">
              {reviews.length > 0 && (
                <>
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < Math.floor(product.rating || 0)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>

                  <span className="text-gray-600">
                    ({reviews.length} {t('productDetail.reviews')})
                  </span>
                </>
              )}

              {/* <span className="text-[#a7cf26] font-semibold">
                {product.sold ?? 0} {t('productDetail.sold')}
              </span> */}
            </div>

            {/* Price + Installment */}
            <div className="mb-2">
              <div className="flex flex-col xl:flex-row xl:items-start gap-5">
                {/* Price */}
                <div className="flex-1">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4 flex-wrap mt-3">
                      <span
                        className={`text-2xl md:text-4xl font-bold ${product.originalPrice && Number(product.originalPrice) > Number(product.price)
                          ? 'text-red-600'
                          : 'text-[#a7cf26]'
                          }`}
                      >
                        {product.price * quantity} MDL
                      </span>

                      {hasDiscount && (
  <span className="text-xl md:text-2xl text-gray-400 line-through">
    {product.originalPrice * quantity} MDL
  </span>
)}
                    </div>

                    <a
                      href="tel:+37367818180"
                      className="w-fit max-md:w-full flex flex-wrap items-center justify-center gap-2 md:gap-3 border-2 border-[#a7cf26] rounded-2xl px-4 md:px-6 py-3 md:py-4 md:mt-[20px] transition"
                    >
                      <span className="text-sm md:text-lg font-semibold text-[#a7cf26]">
                        {t('productDetail.orderByPhone')}
                      </span>

                      <Phone className="text-[#a7cf26] w-5 h-5 md:w-6 md:h-6" />

                      <span className="text-sm md:text-lg font-bold text-red-600">
                        067 81 81 80
                      </span>
                    </a>
                  </div>
                </div>

                {/* Installment Plan Box */}
                <div className="xl:w-[325px] flex-shrink-0">
                  <div className="bg-gradient-to-br from-lime-50 to-lime-100 border-2 border-lime-300 rounded-3xl p-5 shadow-sm">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-semibold uppercase tracking-wide text-lime-700 mb-1">
                            {language === 'ru' ? 'Оплата в 3 платежа' : 'Achită în 3 rate egale'}
                          </p>

                          {/* <p className="text-sm text-gray-600">
                            {language === 'ru' ? 'без процентов' : 'fără dobândă'}
                          </p> */}
                        </div>

                        <div className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-4xl font-black text-gray-900 leading-none">
                              {((product.price * quantity) / 3).toFixed(0)}
                            </span>

                            <div className="flex flex-col justify-center leading-none">
                              <span className="text-lg font-bold text-gray-700 leading-none">
                                MDL
                              </span>

                              <span className="text-xs text-left font-medium text-gray-500 mt-[2px]">
                                {language === 'ru' ? '/ мес' : '/ lună'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => setShowInstallmentModal(true)}
                        className="w-full bg-lime-500 hover:bg-lime-600 text-white py-3 rounded-2xl font-bold transition"
                      >
                        {language === 'ru' ? 'Подробнее' : 'Vezi detalii'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-6 mt-4">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">
                    {t('productDetail.quantity')}
                  </label>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div
                      className={`flex items-center border-2 rounded-lg ${product.available
                        ? 'border-gray-300'
                        : 'border-gray-200 opacity-50 cursor-not-allowed'
                        }`}
                    >
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={!product.available}
                        className="p-3 hover:bg-gray-100 transition disabled:cursor-not-allowed"
                      >
                        <Minus className="w-4 h-4 md:w-5 md:h-5" />
                      </button>

                      <span className="px-3 md:px-6 py-2 font-bold text-lg">
                        {quantity}
                      </span>

                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        disabled={!product.available}
                        className="p-3 hover:bg-gray-100 transition disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:min-w-[230px]">
                  <img
                    src="https://crediport.bg/imgs/LogoIutecredit.webp"
                    alt="Iute credit"
                    className="w-full h-12 object-contain rounded-xl bg-white border-2 border-lime-200 p-2"
                  />

                  <img
                    src="https://i.postimg.cc/SsZVmQxT/Captura-de-ecran-din-2026-05-21-la-10-51-13-Photoroom.png"
                    alt="Virtula"
                    className="w-full h-12 object-contain rounded-xl bg-white border-2 border-lime-200 p-2"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 mb-6">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleAddToCart}
                  className="bg-white border-2 border-[#a7cf26] text-[#a7cf26] py-4 rounded-xl hover:bg-[#a7cf26]/10 transition font-bold text-sm md:text-lg flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-4 h-4 md:w-6 md:h-6" />
                  {t('productDetail.addToCart')}
                </button>

                <button
                  onClick={handleBuyNow}
                  className="bg-[#a7cf26]/90 text-white py-4 rounded-xl hover:bg-[#a7cf26] transition font-bold text-sm md:text-lg"
                >
                  {t('productDetail.buyNow')}
                </button>
              </div>
            </div>

{/* Delivery Methods */}
<div className="bg-white border border-gray-200 rounded-[22px] md:rounded-[28px] p-3 md:p-7 mb-4">
  <div className="space-y-2 md:space-y-5">

    {/* Ridicare personală */}
    <div className="flex items-center justify-between gap-2 md:gap-4">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg md:rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
          <Store className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
        </div>

        <h4 className="text-[12px] sm:text-sm md:text-[16px] font-bold text-gray-900 truncate">
          {language === 'ru' ? 'Самовывоз' : 'Ridicare personală'}
        </h4>
      </div>

      <span className="text-lime-500 font-bold text-[12px] sm:text-sm md:text-[16px] whitespace-nowrap">
        {language === 'ru' ? 'Бесплатно' : 'Gratuit'}
      </span>
    </div>

    {/* Livrare EVS */}
    <div className="flex items-center justify-between gap-2 md:gap-4">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <img
          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTHkDGeP0RKmhUlh-XSDaPnc9bBhVdH7wLZXA&s"
          alt="EVS"
          className="w-7 h-7 md:w-8 md:h-8 object-contain rounded-lg md:rounded-[10px] flex-shrink-0"
        />

        <h4 className="text-[12px] sm:text-sm md:text-[16px] font-bold text-gray-900 truncate">
          {language === 'ru' ? 'Доставка курьером EVS' : 'Livrare cu EVS Curier'}
        </h4>
      </div>

      <div className="flex items-center gap-2 md:gap-10 flex-shrink-0">
        <span className="text-gray-500 text-[11px] sm:text-sm md:text-[16px] whitespace-nowrap">
          {language === 'ru' ? '1–3 дня' : '1–3 zile'}
        </span>

        <span className="font-bold text-gray-900 text-[12px] sm:text-sm md:text-[16px] whitespace-nowrap">
          {language === 'ru' ? 'от 70 леев' : 'de la 70 Lei'}
        </span>
      </div>
    </div>

    {/* Livrare FAN */}
    <div className="flex items-center justify-between gap-2 md:gap-4">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <img
          src="https://play-lh.googleusercontent.com/GpEvB5v4rCW5SCxvMwI9MmChv5rPyxtit9eqFtUvHk03DZlJAuDlfQ4vaEk0w3ZPBI8"
          alt="FAN Courier"
          className="w-7 h-7 md:w-8 md:h-8 object-contain rounded-lg md:rounded-[10px] flex-shrink-0"
        />

        <h4 className="text-[12px] sm:text-sm md:text-[16px] font-bold text-gray-900 truncate">
          {language === 'ru' ? 'Доставка курьером FAN' : 'Livrare cu FAN Curier'}
        </h4>
      </div>

      <div className="flex items-center gap-2 md:gap-10 flex-shrink-0">
        <span className="text-gray-500 text-[11px] sm:text-sm md:text-[16px] whitespace-nowrap">
          {language === 'ru' ? '2–3 дня' : '2–3 zile'}
        </span>

        <span className="font-bold text-gray-900 text-[12px] sm:text-sm md:text-[16px] whitespace-nowrap">
          {language === 'ru' ? 'от 70 леев' : 'de la 70 Lei'}
        </span>
      </div>
    </div>

  </div>
</div>

{/* Delivery FAQ */}
{deliveryFaqItems.length > 0 && (
  <div className="bg-white border border-gray-200 rounded-[22px] md:rounded-[28px] p-4 md:p-7 mb-4">
    {/* <h3 className="text-[16px] md:text-[20px] font-extrabold text-gray-900 mb-4">
      {language === 'ru' ? 'Частые вопросы' : 'Întrebări frecvente'}
    </h3> */}

    <div className="space-y-2">
      {deliveryFaqItems.map((item, index) => {
        const isOpen = openFaqIndex === index;

        return (
          <div
            key={index}
            className="border border-gray-100 rounded-2xl overflow-hidden bg-gray-50"
          >
            <button
              type="button"
              onClick={() => setOpenFaqIndex(isOpen ? null : index)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <span className="text-[13px] md:text-[15px] font-bold text-gray-900">
                {item.question}
              </span>

              <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <Plus
                  className={`w-4 h-4 text-[#a7cf26] transition-transform duration-300 ${
                    isOpen ? 'rotate-45' : 'rotate-0'
                  }`}
                />
              </span>
            </button>

            <div
              className={`grid transition-all duration-300 ease-in-out ${
                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="px-4 pb-4 text-[13px] md:text-[14px] leading-relaxed text-gray-600">
                  {item.answer}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
)}

          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl p-4 md:p-8 mb-12">
          <div className="flex gap-5 md:gap-8 border-b mb-6 overflow-x-auto">
            {['specifications', 'description', 'reviews'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 font-semibold transition border-b-2 whitespace-nowrap ${activeTab === tab
                  ? 'border-[#a7cf26] text-[#a7cf26]'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
              >
                {tab === 'description' && t('productDetail.tabs.description')}
                {tab === 'specifications' &&
                  t('productDetail.tabs.specifications')}
                {tab === 'reviews' && t('productDetail.tabs.reviews')}
              </button>
            ))}
          </div>

          <div className="prose max-w-none">
            {activeTab === 'description' && (() => {
              const description =
                language === 'ru' && product.descriptionRu
                  ? product.descriptionRu
                  : product.description;

              if (!description) {
                return (
                  <p className="text-gray-600">
                    {t('productDetail.noDescription')}
                  </p>
                );
              }

              return (
                <div
  className="max-w-full overflow-hidden break-words text-gray-700 leading-snug md:leading-normal [overflow-wrap:anywhere] [&_*]:max-w-full [&_p]:my-1.5 [&_div]:my-1.5 [&_br]:leading-[1.2] [&_strong]:font-bold [&_em]:italic [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1 [&_h1]:mb-3 [&_h1]:text-2xl md:[&_h1]:text-3xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:text-lg md:[&_h3]:text-xl [&_h3]:font-bold [&_img]:h-auto [&_img]:max-w-full [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto"
  dangerouslySetInnerHTML={{ __html: description }}
/>
              );
            })()}

            {activeTab === 'specifications' && (
              <div>
                {product.specifications && product.specifications.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {product.specifications.map((spec, index) => {
                      const specTitle =
                        language === 'ru' && spec.titleRu
                          ? spec.titleRu
                          : spec.title;

                      const specValue =
                        language === 'ru' && spec.valueRu
                          ? spec.valueRu
                          : spec.value;

                      return (
                        <div key={index} className="border-b pb-2">
                          <strong className="text-gray-900">{specTitle}:</strong>
                          <span className="ml-2 text-gray-700">{specValue}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-600">
                    {t('productDetail.noSpecifications')}
                  </p>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-xl p-5 md:p-6 h-fit">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {t('productDetail.writeReview')}
                  </h3>

                  <form onSubmit={handleSubmitReview} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('productDetail.yourName')} *
                        </label>

                        <input
                          type="text"
                          required
                          value={reviewForm.userName}
                          onChange={(e) =>
                            setReviewForm({
                              ...reviewForm,
                              userName: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]/90"
                          placeholder="Ion Popescu"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('productDetail.email')} *
                        </label>

                        <input
                          type="email"
                          required
                          value={reviewForm.userEmail}
                          onChange={(e) =>
                            setReviewForm({
                              ...reviewForm,
                              userEmail: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]/90"
                          placeholder="email@exemplu.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('productDetail.rating')} *
                      </label>

                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() =>
                              setReviewForm({ ...reviewForm, rating: star })
                            }
                            className="focus:outline-none"
                          >
                            <Star
                              className={`w-8 h-8 ${star <= reviewForm.rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                                }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('productDetail.comment')} *
                      </label>

                      <textarea
                        required
                        value={reviewForm.comment}
                        onChange={(e) =>
                          setReviewForm({
                            ...reviewForm,
                            comment: e.target.value,
                          })
                        }
                        rows="4"
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a7cf26]/90"
                        placeholder={t('productDetail.commentPlaceholder')}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submittingReview}
                      className="bg-[#a7cf26]/90 text-white px-6 py-3 rounded-lg hover:bg-[#a7cf26] transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingReview
                        ? t('productDetail.submitting')
                        : t('productDetail.submitReview')}
                    </button>
                  </form>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    {t('productDetail.customerReviews')}
                  </h3>

                  {reviews.length > 0 ? (
                    <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                      {reviews.map((review) => (
                        <div key={review.id} className="border-b pb-6">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                {review.userName}
                              </h4>

                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${i < review.rating
                                        ? 'text-yellow-400 fill-yellow-400'
                                        : 'text-gray-300'
                                        }`}
                                    />
                                  ))}
                                </div>

                                <span className="text-sm text-gray-500">
                                  {new Date(
                                    review.createdAt
                                  ).toLocaleDateString('ro-RO')}
                                </span>
                              </div>
                            </div>
                          </div>

                          <p className="text-gray-700">{review.comment}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-600 bg-gray-50 rounded-lg">
                      {t('productDetail.noReviews')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>


        <div className="w-full pb-[30px]">
  <div className="rounded-2xl border border-yellow-300 bg-yellow-50 px-4 py-4 md:px-6 md:py-5">
    <div className="flex items-start gap-3 text-yellow-900">
      <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-yellow-500 text-[13px] font-bold italic text-yellow-500">
        i
      </div>

      <p className="text-[14px] md:text-[18px] font-semibold leading-relaxed">
        {language === 'ru'
          ? `Характеристики и изображения товара ${productName} представлены для ознакомления и могут отличаться от реального товара. Рекомендуем связаться с нами перед оформлением заказа для уточнения нужных характеристик.`
          : `Specificațiile și imaginile pentru ${productName} sunt pentru prezentare și pot diferi de produsul real. Vă recomandăm să ne telefonați pentru concretizarea specificațiilor dorite înainte de finalizarea comenzii.`}
      </p>
    </div>
  </div>
</div>

        {/* Related Products Carousel */}
        {relatedProducts.length > 0 && (
          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                {t('productDetail.relatedProducts')}
              </h2>

              {relatedProducts.length > 5 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => scrollRelatedProducts('left')}
                    className="p-2 rounded-full bg-[#a7cf26]/10 hover:bg-gray-200 border border-[#a7cf26] transition"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={() => scrollRelatedProducts('right')}
                    className="p-2 rounded-full bg-[#a7cf26]/10 hover:bg-gray-200 border border-[#a7cf26] transition"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div
              id="related-products-container"
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {relatedProducts.map((prod) => (
                <div
                  key={prod.id}
                  className="flex-shrink-0 w-[70%] sm:w-[45%] md:w-[30%] lg:w-[20%]"
                >
                  <ProductCard product={prod} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Installment Plan Modal */}
      {showInstallmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-30">
              <h3 className="relative z-10 text-xl font-bold text-gray-900">
                {t('productDetail.installmentModal.title')} - {productName}
              </h3>

              <button
                onClick={() => setShowInstallmentModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">
                  {t('productDetail.installmentModal.plan')}
                </h4>

                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">
                      {t('productDetail.installmentModal.total')}:
                    </span>

                    <span className="text-2xl font-bold text-orange-600">
                      {(product.price * quantity).toFixed(2)} MDL
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-900">
                      {t('productDetail.installmentModal.monthly')}:
                    </span>

                    <span className="text-xl font-bold text-[#a7cf26]">
                      {((product.price * quantity) / 3).toFixed(2)} MDL
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-base md:text-lg font-bold text-gray-900 mb-4">
                  {t('productDetail.installmentModal.schedule')}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  {[1, 2, 3].map((month) => (
                    <div
                      key={month}
                      className="bg-gray-50 rounded-xl p-3 md:p-4 text-center"
                    >
                      <div className="relative z-0 w-16 h-16 md:w-24 md:h-24 mx-auto mb-2 md:mb-3">
                        <svg
                          viewBox="0 0 100 100"
                          className="w-full h-full transform -rotate-90 relative z-0"
                        >
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#E5E7EB"
                            strokeWidth="10"
                          />

                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke={
                              month === 1
                                ? '#F97316'
                                : month === 2
                                  ? '#14B8A6'
                                  : '#06B6D4'
                            }
                            strokeWidth="10"
                            strokeDasharray={`${(month / 3) * 251.2} 251.2`}
                          />
                        </svg>

                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm md:text-2xl font-bold text-gray-900">
                            {month}/3
                          </span>
                        </div>
                      </div>

                      <p className="font-bold text-gray-900 mb-1 text-sm md:text-base">
                        {month === 1
                          ? language === 'ru'
                            ? 'При получении'
                            : 'La primire'
                          : `${t('productDetail.installmentModal.month')} ${month}`}
                      </p>

                      <p className="text-sm md:text-lg font-bold text-[#a7cf26]">
                        {((product.price * quantity) / 3).toFixed(2)} MDL
                      </p>

                      <p className="text-[11px] md:text-xs text-gray-500 mt-1">
                        {new Date(
  Date.now() + (month - 1) * 30 * 24 * 60 * 60 * 1000
).toLocaleDateString('ro-RO', {
  day: 'numeric',
  month: 'short',
})}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#a7cf26]/5 border-2 border-[#a7cf26]/20 rounded-xl p-6 mb-4">
                <h4 className="text-lg font-bold text-gray-900 mb-4">
                  {t('productDetail.installmentModal.request')}
                </h4>

                <form
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();

                    try {
                      await axios.post(`${API}/installment/request`, {
                        productId: product.id,
                        productName: product.name,
                        productPrice: product.price * quantity,
                        quantity: quantity,
                        name: installmentForm.name,
                        phone: installmentForm.phone,
                      });

                      toast({
                        title: t('productDetail.installmentModal.success'),
                        description: `${t('checkout.thankYou') || 'Mulțumim'
                          } ${installmentForm.name}!`,
                      });

                      setShowInstallmentModal(false);
                      setInstallmentForm({ name: '', phone: '' });
                    } catch (error) {
                      toast({
                        title: t('auth.error'),
                        description: t('productDetail.installmentModal.error'),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      {t('productDetail.installmentModal.fullName')} *
                    </label>

                    <input
                      type="text"
                      required
                      value={installmentForm.name}
                      onChange={(e) =>
                        setInstallmentForm({
                          ...installmentForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]/90"
                      placeholder="Ion Popescu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      {t('productDetail.installmentModal.phone')} *
                    </label>

                    <input
                      type="tel"
                      required
                      value={installmentForm.phone}
                      onChange={(e) =>
                        setInstallmentForm({
                          ...installmentForm,
                          phone: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a7cf26]/90"
                      placeholder="+373 69 123 456"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#a7cf26]/90 text-white py-3 rounded-xl hover:bg-[#a7cf26] transition font-bold text-lg"
                  >
                    {t('productDetail.installmentModal.send')}
                  </button>

                  <p className="text-xs text-gray-600 text-center">
                    {t('productDetail.installmentModal.note')}
                  </p>
                </form>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  'noInterest',
                  'noFees',
                  'fastApproval',
                  'online',
                ].map((benefit) => (
                  <div
                    key={benefit}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <svg
                      className="w-5 h-5 text-green-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>

                    <span>
                      {t(
                        `productDetail.installmentModal.benefits.${benefit}`
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

{/* Image Gallery Modal */}
{isGalleryOpen && (
  <div
    className="fixed inset-0 bg-black/95 flex items-center justify-center z-[999] p-4 md:p-6"
    onClick={closeGallery}
  >
    {/* CLOSE */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        closeGallery();
      }}
      className="
        absolute
        top-3
        right-3
        md:top-5
        md:right-5
        p-2.5
        md:p-3
        bg-white/15
        hover:bg-white/25
        rounded-full
        transition
        z-[1000]
      "
      aria-label="Închide"
    >
      <X className="w-5 h-5 md:w-6 md:h-6 text-white" />
    </button>

    {/* PREVIOUS */}
    {images.length > 1 && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          prevImage(e);
        }}
        className="
          absolute
          left-2
          md:left-5
          top-1/2
          -translate-y-1/2
          p-2
          md:p-3
          bg-white/15
          hover:bg-white/25
          rounded-full
          transition
          z-[1000]
        "
        aria-label="Imaginea anterioară"
      >
        <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
      </button>
    )}

    {/* CONTENT */}
    <div
      className="
        relative
        flex
        flex-col
        items-center
        justify-center
        w-full
        max-w-[95vw]
        md:max-w-[92vw]
        lg:max-w-[1200px]
        max-h-[95vh]
      "
      onClick={(e) => e.stopPropagation()}
    >
      {/* IMAGE */}
      <div
  className="
    flex
    items-center
    justify-center
    max-w-[95vw]
    max-h-[78vh]
    overflow-hidden
  "
>
  <img
    src={images[galleryIndex]}
    alt={productName}
    className="
      block
      max-w-[95vw]
      max-h-[78vh]
      w-auto
      h-auto
      object-contain
      select-none
    "
  />
</div>

      {/* CAPTION SUB IMAGINE — folosește galleryCaption din admin; dacă e gol, cade pe numele produsului */}
      <div className="w-full px-10 md:px-16 pt-3 md:pt-4 pb-1">
        <h2
          className="
            text-white
            text-center
            text-sm
            md:text-lg
            lg:text-xl
            font-semibold
            leading-snug
            line-clamp-2
          "
          data-testid="gallery-caption"
        >
          {(language === 'ru' && product.galleryCaptionRu) ||
            product.galleryCaption ||
            productName}
        </h2>
      </div>

      {/* IMAGE COUNTER */}
      {images.length > 1 && (
        <div
          className="
            mt-2
            md:mt-3
            px-3
            py-1
            bg-white/15
            rounded-full
            text-white
            text-xs
            md:text-sm
            font-semibold
          "
        >
          {galleryIndex + 1} / {images.length}
        </div>
      )}
    </div>

    {/* NEXT */}
    {images.length > 1 && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          nextImage(e);
        }}
        className="
          absolute
          right-2
          md:right-5
          top-1/2
          -translate-y-1/2
          p-2
          md:p-3
          bg-white/15
          hover:bg-white/25
          rounded-full
          transition
          z-[1000]
        "
        aria-label="Imaginea următoare"
      >
        <ChevronRight className="w-6 h-6 md:w-8 md:h-8 text-white" />
      </button>
    )}
  </div>
)}

      <GiftPopup product={product} />
    </div>
  );
};

export default ProductDetailPage;